import React, { useContext } from "react";
import { isEmpty } from "lodash";

import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';

import { ConfigContext } from "../Config/ConfigContext";
import FormDialog from "../form/form-dialog";
import FormInput from "../form/form-input";
import auth from "../utils/auth";
import http from "../utils/http";
import { parseJwt } from "../utils/jwt";
import useForm from "../hooks/useForm";
import {
    Add
} from "@mui/icons-material";

import {
    Box,
    Button,
    Container,
    IconButton,
    MenuItem,
    Menu,
    Typography,
} from "@mui/material";
import Members from "./members";


const addOrganizationSubmit = async ({
    values,
    onFail,
    onInvalidForm,
    onChangeBulk,
    onResetFormErrors,
}) => {
    onChangeBulk({
        statusType: '',
        statusMessage: '',
    });

    let errors = {};

    if (!values.newOrgName) errors.newOrgName = 'Organization Name must be populated';
    if (!isEmpty(errors)) return onInvalidForm(errors);

    onResetFormErrors();

    const result = await http.post({
        name: values.newOrgName,
    }, `${values.config.uri.base}/tenant`);
    if (result.errored) return onFail(result);

    const tenantResults = await http.get({}, `${values.config.uri.base}/tenant?pageOffset=0&pageSize=5`);
    let options = tenantResults.rows.map(x => {
        return {
            label: x.name,
            value: x._id,
        };
    });

    onChangeBulk({
        newOrgName: '',
        addOrgModalIsOpen: false,
        modalToggle: false,
        statusType: 'success',
        statusMessage: `Successfully saved new Organization: ${values.newOrgName}`,
        tenants: options,
        _id: result._id,
    });

};


const onLoad = async ({
    values
}) => {
    const result = await http.get({
        tenantNameFilter: values.tenantNameFilter,
        pageSize: 5,
        pageOffset: 0,
    }, `${values.config.uri.base}/tenant`);
    let options = result.rows.map(x => {
        return {
            label: x.name,
            value: x._id,
        };
    });

    const currentWorkspace = await http.get({}, `${values.config.uri.base}/tenant/${values.tenantId}`);
    return {
        ...values,
        tenants: options,
        currentWorkspaceName: currentWorkspace.name,
    };
}


export default function MultiTenant() {
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const handleOpenMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorElUser(null);
    };

    const { config } = useContext(ConfigContext);
    const {
        state,
        onChange,
        onInvalidForm,
        onFail,
        onChangeBulk,
        onResetFormErrors,
    } = useForm({
        debug: true,
        initialState: {
            currentWorkspaceName: '',
            tenants: [],
            tenantId: parseJwt(auth.getBearerToken()).tenantId,
            tenantNameFilter: '',
            manageOrgName: '',
            config,
            addOrgModalIsOpen: false,
            manageOrgModalIsOpen: false,
        },
        onLoad,
        stateToTriggerOnLoad: ['tenantNameFilter'],
    });

    const handleCloseModal = ({ openedState = false }) => {
        handleCloseMenu();
        onChange(openedState, 'addOrgModalIsOpen');
    };

    const handleCloseManageModal = ({ openedState = false }) => {
        handleCloseMenu();
        onChange(openedState, 'manageOrgModalIsOpen');
    };

    return (
        <Container className="multitenant-container" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
                onClick={handleOpenMenu}
            >
                {state.values.currentWorkspaceName}
            </Button>
            <IconButton
                onClick={() => {
                    onChangeBulk({
                        manageOrgModalIsOpen: true,
                        manageOrgName: state.values.currentWorkspaceName,
                        manageTenantId: state.values.tenantId,
                    });
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
                </svg>
            </IconButton>
            <Menu
                sx={{
                    mt: '45px',
                    padding: '0px 10px',
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseMenu}
            >
                <MenuItem onKeyDown={(e) => e.stopPropagation()}>
                    <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        placeholder="Search workspaces"
                        inputProps={{ 'aria-label': 'search workspaces' }}
                        value={state.values.tenantNameFilter}
                        onChange={(e) => {
                            onChange(e.target.value, 'tenantNameFilter');
                        }}
                    />
                    <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
                        <SearchIcon />
                    </IconButton>
                </MenuItem>
                {state.values.tenants.map((setting) => (
                    <MenuItem
                        onClick={async () => {
                            handleCloseMenu(setting);
                            onChange(setting.value, 'tenantId');
                            const platformJWT = await http.get({}, `${config.uri.base}/tenant/switch?tenantId=${setting.value}`, { withCredentials: true });
                            auth.setBearerToken(platformJWT);
                            window.location.reload();
                        }}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            width: '100%',
                        }}
                        key={setting.value}>

                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                borderBottom: '1px solid #000',
                            }}>
                            <Box
                                display="flex"
                                justifyContent="space-around"
                            >
                                <Typography
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >{setting.label}</Typography>
                                {state.values.tenantId === setting.value && <Typography>✔</Typography>}
                            </Box>
                            {state.values.tenantId === setting.value &&
                                <Typography
                                    fontSize="small"
                                    fontStyle="italic"
                                    fontWeight="bold"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >Current Workspace</Typography>}
                            {state.values.tenantId !== setting.value &&
                                <Typography
                                    fontSize="small"
                                    fontStyle="italic"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >Switch Workspace</Typography>}
                        </Box>
                    </MenuItem>
                ))}
                <MenuItem key={state.values.tenants.length + 1}
                    onClick={() => handleCloseModal({ openedState: true })}
                >
                    <Typography textAlign="center">
                        Create Workspace
                        <IconButton>
                            <Add />
                        </IconButton>
                    </Typography>
                </MenuItem>
            </Menu>
            <FormDialog
                dialogTitle={'Create Organization'}
                dialogSubHeading={'Create an organization to house all your private data.'}
                handleClose={handleCloseModal}
                handleSubmit={() => addOrganizationSubmit({
                    values: state.values,
                    onInvalidForm,
                    onFail,
                    onChangeBulk,
                    onResetFormErrors,
                })}
                isOpen={state.values.addOrgModalIsOpen}
            >
                <FormInput
                    path='newOrgName'
                    value={state.values.newOrgName}
                    error={state.errors.newOrgName}
                    onChange={onChange}
                />
            </FormDialog>
            <FormDialog
                dialogTitle={`Manage '${state.values.manageOrgName}'`}
                dialogSubHeading={'Invite users to your organization.'}
                cancelButtonText={'Dismiss'}
                handleClose={handleCloseManageModal}
                isOpen={state.values.manageOrgModalIsOpen}
            >
                <Members
                    tenantId={state.values.manageTenantId}
                />
            </FormDialog>

        </Container>
    );
}
